import { REGISTERED_MODULES } from "../modules/index.js";
import { composeProfiles } from "../modules/compose.js";
import { CORE_MODULES, CORE_ONBOARDING } from "./core-modules.js";
import { BUSINESS_TYPES } from "../constants/business-types.js";
/**
 * Counter trade and goods trade, the two axes that decide the till and
 * the inventory.
 *
 * `pos` follows `BUSINESS_TYPES.<CODE>.defaultPosMode` exactly — a test
 * asserts the equivalence over all 18 profiles, so the two never drift.
 * `stock` follows whether the sector holds goods: merchandise for resale,
 * or raw material it consumes to deliver its service.
 *
 * Three axes carry sector-specific fiscal and regulatory content:
 *
 * `audcifClass` — which AUDCIF art. 13 turnover ceiling would apply. The
 * discriminator is what the entity does with what it sells: resells goods
 * in the state received (négoce), transforms material or plies a manual
 * trade (artisanale), or delivers an intangible (services).
 *
 * `defaultTaxGroup` — the group a new article starts on. Group B, the
 * 18 % standard rate, unless a text sets another for the sector itself.
 * A rate that depends on a per-tenant approval or a per-article
 * classification is never a sector fact, so it goes to
 * `conditionalTaxGroup`, which states the condition and applies nothing.
 *
 * `obligations` — dated, renewable obligations, as data. Nothing reads
 * them yet: no reminder, no schedule, no screen.
 *
 * An empty `terminology` is a statement, not an omission: the general
 * trade profiles sell « articles » to « clients » out of « stock », so
 * the base catalogue already holds their words and an overlay would only
 * paraphrase them. Overrides also stay off regulated vocabulary —
 * « facture », « avoir » and the DGI invoice and article types keep the
 * wording the texts give them, which a test enforces.
 */
const BASE_PROFILES = {
    DETAIL: {
        code: "DETAIL",
        label: BUSINESS_TYPES.DETAIL.label,
        description: BUSINESS_TYPES.DETAIL.description,
        icon: "store",
        modules: [...CORE_MODULES, "pos", "stock", "depots", "inventories"],
        onboarding: [...CORE_ONBOARDING],
        terminology: {},
        accountingSystem: "NORMAL",
        defaultPosMode: BUSINESS_TYPES.DETAIL.defaultPosMode,
        defaultPriceMode: BUSINESS_TYPES.DETAIL.defaultPosMode ? "TTC" : "HT",
        psvbApplicable: BUSINESS_TYPES.DETAIL.psvbApplicable,
        categories: BUSINESS_TYPES.DETAIL.categories,
        audcifClass: "negoce",
        defaultTaxGroup: "B",
    },
    GROS: {
        code: "GROS",
        label: BUSINESS_TYPES.GROS.label,
        description: BUSINESS_TYPES.GROS.description,
        icon: "warehouse",
        modules: [...CORE_MODULES, "stock", "depots", "inventories"],
        onboarding: [...CORE_ONBOARDING],
        terminology: {},
        accountingSystem: "NORMAL",
        defaultPosMode: BUSINESS_TYPES.GROS.defaultPosMode,
        defaultPriceMode: BUSINESS_TYPES.GROS.defaultPosMode ? "TTC" : "HT",
        psvbApplicable: BUSINESS_TYPES.GROS.psvbApplicable,
        categories: BUSINESS_TYPES.GROS.categories,
        audcifClass: "negoce",
        defaultTaxGroup: "B",
    },
    DEMI_GROS: {
        code: "DEMI_GROS",
        label: BUSINESS_TYPES.DEMI_GROS.label,
        description: BUSINESS_TYPES.DEMI_GROS.description,
        icon: "boxes",
        modules: [...CORE_MODULES, "stock", "depots", "inventories"],
        onboarding: [...CORE_ONBOARDING],
        terminology: {},
        accountingSystem: "NORMAL",
        defaultPosMode: BUSINESS_TYPES.DEMI_GROS.defaultPosMode,
        defaultPriceMode: BUSINESS_TYPES.DEMI_GROS.defaultPosMode ? "TTC" : "HT",
        psvbApplicable: BUSINESS_TYPES.DEMI_GROS.psvbApplicable,
        categories: BUSINESS_TYPES.DEMI_GROS.categories,
        audcifClass: "negoce",
        defaultTaxGroup: "B",
    },
    ALIMENTATION: {
        code: "ALIMENTATION",
        label: BUSINESS_TYPES.ALIMENTATION.label,
        description: BUSINESS_TYPES.ALIMENTATION.description,
        icon: "shopping-basket",
        modules: [...CORE_MODULES, "pos", "stock", "depots", "inventories"],
        onboarding: [...CORE_ONBOARDING],
        terminology: {},
        accountingSystem: "NORMAL",
        defaultPosMode: BUSINESS_TYPES.ALIMENTATION.defaultPosMode,
        defaultPriceMode: BUSINESS_TYPES.ALIMENTATION.defaultPosMode ? "TTC" : "HT",
        psvbApplicable: BUSINESS_TYPES.ALIMENTATION.psvbApplicable,
        categories: BUSINESS_TYPES.ALIMENTATION.categories,
        audcifClass: "negoce",
        defaultTaxGroup: "B",
    },
    BOUTIQUE: {
        code: "BOUTIQUE",
        label: BUSINESS_TYPES.BOUTIQUE.label,
        description: BUSINESS_TYPES.BOUTIQUE.description,
        icon: "shopping-bag",
        modules: [...CORE_MODULES, "pos", "stock", "depots", "inventories"],
        onboarding: [...CORE_ONBOARDING],
        terminology: {},
        accountingSystem: "NORMAL",
        defaultPosMode: BUSINESS_TYPES.BOUTIQUE.defaultPosMode,
        defaultPriceMode: BUSINESS_TYPES.BOUTIQUE.defaultPosMode ? "TTC" : "HT",
        psvbApplicable: BUSINESS_TYPES.BOUTIQUE.psvbApplicable,
        categories: BUSINESS_TYPES.BOUTIQUE.categories,
        audcifClass: "negoce",
        defaultTaxGroup: "B",
    },
    PHARMACIE: {
        code: "PHARMACIE",
        label: BUSINESS_TYPES.PHARMACIE.label,
        description: BUSINESS_TYPES.PHARMACIE.description,
        icon: "pill",
        modules: [...CORE_MODULES, "pos", "stock", "depots", "inventories"],
        onboarding: [...CORE_ONBOARDING],
        terminology: {
            "pos.title": "Comptoir de l'officine",
            "nav.stock": "Stock & péremptions",
            "stock.receive.expirationDate": "Date de péremption (optionnel)",
        },
        accountingSystem: "NORMAL",
        defaultPosMode: BUSINESS_TYPES.PHARMACIE.defaultPosMode,
        defaultPriceMode: BUSINESS_TYPES.PHARMACIE.defaultPosMode ? "TTC" : "HT",
        psvbApplicable: BUSINESS_TYPES.PHARMACIE.psvbApplicable,
        categories: BUSINESS_TYPES.PHARMACIE.categories,
        audcifClass: "negoce",
        // CGI art. 308 exonerates médicaments and produits pharmaceutiques by
        // SH code (docs/fiscal/2026-07-12-tva-facture-electronique.md:14), so
        // the exoneration attaches to the article, not to the counter it is
        // sold over. An officine also sells what the list does not cover.
        defaultTaxGroup: "A",
        conditionalTaxGroup: {
            group: "B",
            condition: "Articles hors liste SH de l'article 308 : parapharmacie, cosmétiques, matériel",
            source: "CGI art. 308 — docs/fiscal/2026-07-12-tva-facture-electronique.md:14",
        },
    },
    LIBRAIRIE: {
        code: "LIBRAIRIE",
        label: BUSINESS_TYPES.LIBRAIRIE.label,
        description: BUSINESS_TYPES.LIBRAIRIE.description,
        icon: "book-open",
        modules: [...CORE_MODULES, "stock", "depots", "inventories"],
        onboarding: [...CORE_ONBOARDING],
        terminology: {
            "articles.title": "Ouvrages & fournitures",
        },
        accountingSystem: "NORMAL",
        defaultPosMode: BUSINESS_TYPES.LIBRAIRIE.defaultPosMode,
        defaultPriceMode: BUSINESS_TYPES.LIBRAIRIE.defaultPosMode ? "TTC" : "HT",
        psvbApplicable: BUSINESS_TYPES.LIBRAIRIE.psvbApplicable,
        categories: BUSINESS_TYPES.LIBRAIRIE.categories,
        audcifClass: "negoce",
        defaultTaxGroup: "B",
    },
    QUINCAILLERIE: {
        code: "QUINCAILLERIE",
        label: BUSINESS_TYPES.QUINCAILLERIE.label,
        description: BUSINESS_TYPES.QUINCAILLERIE.description,
        icon: "hammer",
        modules: [...CORE_MODULES, "stock", "depots", "inventories"],
        onboarding: [...CORE_ONBOARDING],
        terminology: {
            "articles.title": "Matériaux & outillage",
        },
        accountingSystem: "NORMAL",
        defaultPosMode: BUSINESS_TYPES.QUINCAILLERIE.defaultPosMode,
        defaultPriceMode: BUSINESS_TYPES.QUINCAILLERIE.defaultPosMode ? "TTC" : "HT",
        psvbApplicable: BUSINESS_TYPES.QUINCAILLERIE.psvbApplicable,
        categories: BUSINESS_TYPES.QUINCAILLERIE.categories,
        audcifClass: "negoce",
        defaultTaxGroup: "B",
    },
    STATION: {
        code: "STATION",
        label: BUSINESS_TYPES.STATION.label,
        description: BUSINESS_TYPES.STATION.description,
        icon: "fuel",
        modules: [...CORE_MODULES, "pos", "stock", "depots", "inventories"],
        onboarding: [...CORE_ONBOARDING],
        terminology: {
            "nav.stock": "Cuves & stock",
            "stock.receive.title": "Réception en cuve",
            "articles.title": "Carburants & produits",
        },
        accountingSystem: "NORMAL",
        defaultPosMode: BUSINESS_TYPES.STATION.defaultPosMode,
        defaultPriceMode: BUSINESS_TYPES.STATION.defaultPosMode ? "TTC" : "HT",
        psvbApplicable: BUSINESS_TYPES.STATION.psvbApplicable,
        categories: BUSINESS_TYPES.STATION.categories,
        audcifClass: "negoce",
        defaultTaxGroup: "B",
        obligations: [
            {
                code: "CERTIFICAT_JAUGEAGE",
                label: "Certificat de jaugeage des cuves (BUMIGEB) — nul après réparation ou déformation",
                renewalMonths: 72,
                sourceStatus: "a_verifier",
                source: "arrêté n°154/MMCE/SG/DGE — texte absent du dépôt",
            },
        ],
    },
    RESTAURANT: {
        code: "RESTAURANT",
        label: BUSINESS_TYPES.RESTAURANT.label,
        description: BUSINESS_TYPES.RESTAURANT.description,
        icon: "utensils",
        modules: [...CORE_MODULES, "pos", "stock", "depots", "inventories"],
        onboarding: [...CORE_ONBOARDING],
        terminology: {
            "pos.cart": "Commande",
            "pos.cartEmpty": "Aucune commande en cours",
            "articles.title": "Carte & menu",
        },
        accountingSystem: "NORMAL",
        defaultPosMode: BUSINESS_TYPES.RESTAURANT.defaultPosMode,
        defaultPriceMode: BUSINESS_TYPES.RESTAURANT.defaultPosMode ? "TTC" : "HT",
        psvbApplicable: BUSINESS_TYPES.RESTAURANT.psvbApplicable,
        categories: BUSINESS_TYPES.RESTAURANT.categories,
        // Straddles the AUDCIF classes: it transforms raw material like an
        // artisan and serves it like a service. Classed artisanale on the
        // transformation, which is what the meal it invoices comes from.
        audcifClass: "artisanale",
        // CGI art. 317 as rewritten by the LF2025 reduces the rate to 10 %
        // for hébergement and restauration supplied by establishments that
        // are « agréés » (docs/fiscal/2026-07-12-tva-facture-electronique.md:12).
        // Approval is granted to a taxpayer, not to a trade, so the sector
        // cannot carry the reduced rate as its default.
        defaultTaxGroup: "B",
        conditionalTaxGroup: {
            group: "C",
            condition: "Hébergement et restauration fournis par un établissement agréé",
            source: "CGI art. 317 (LF2025, loi n°042-2024/ALT) — docs/fiscal/2026-07-12-tva-facture-electronique.md:12",
        },
        obligations: [
            {
                code: "CONTRIBUTION_LICENCES",
                label: "Contribution des licences (débit de boissons) — à acquitter avant le 1er mars",
                renewalMonths: 12,
                sourceStatus: "verifie",
                source: "CGI art. 247-250 — docs/fiscal/sources/cgi_v2024_texte.txt:5444",
            },
        ],
    },
    // Every category it sells is a service delivered on the spot
    // (NAVIGATION, IMPRESSION, SAISIE, TRANSFERT): nothing to hold in
    // inventory, and `defaultPosMode` is false, so neither pack applies.
    CYBER: {
        code: "CYBER",
        label: BUSINESS_TYPES.CYBER.label,
        description: BUSINESS_TYPES.CYBER.description,
        icon: "monitor",
        modules: [...CORE_MODULES],
        onboarding: [...CORE_ONBOARDING],
        terminology: {
            "articles.title": "Prestations",
        },
        accountingSystem: "NORMAL",
        defaultPosMode: BUSINESS_TYPES.CYBER.defaultPosMode,
        defaultPriceMode: BUSINESS_TYPES.CYBER.defaultPosMode ? "TTC" : "HT",
        psvbApplicable: BUSINESS_TYPES.CYBER.psvbApplicable,
        categories: BUSINESS_TYPES.CYBER.categories,
        // Three of its four categories are intangible (NAVIGATION, SAISIE,
        // TRANSFERT); IMPRESSION alone would read artisanale. Classed on the
        // majority.
        audcifClass: "services",
        defaultTaxGroup: "B",
    },
    COUTURE: {
        code: "COUTURE",
        label: BUSINESS_TYPES.COUTURE.label,
        description: BUSINESS_TYPES.COUTURE.description,
        icon: "scissors",
        modules: [...CORE_MODULES, "stock", "depots", "inventories"],
        onboarding: [...CORE_ONBOARDING],
        terminology: {
            "nav.stock": "Tissus & fournitures",
            "stock.receive.title": "Entrée de fournitures",
            "articles.title": "Prestations de l'atelier",
        },
        accountingSystem: "NORMAL",
        defaultPosMode: BUSINESS_TYPES.COUTURE.defaultPosMode,
        defaultPriceMode: BUSINESS_TYPES.COUTURE.defaultPosMode ? "TTC" : "HT",
        psvbApplicable: BUSINESS_TYPES.COUTURE.psvbApplicable,
        categories: BUSINESS_TYPES.COUTURE.categories,
        audcifClass: "artisanale",
        defaultTaxGroup: "B",
        obligations: [
            {
                code: "CARTE_ARTISAN",
                label: "Carte professionnelle d'artisan (CMA-BF) — à renouveler",
                sourceStatus: "a_verifier",
                source: "Chambre des Métiers et de l'Artisanat — texte absent du dépôt",
            },
        ],
    },
    SALON: {
        code: "SALON",
        label: BUSINESS_TYPES.SALON.label,
        description: BUSINESS_TYPES.SALON.description,
        icon: "sparkles",
        modules: [...CORE_MODULES, "stock", "depots", "inventories"],
        onboarding: [...CORE_ONBOARDING],
        terminology: {
            "nav.stock": "Produits & consommables",
            "articles.title": "Prestations du salon",
        },
        accountingSystem: "NORMAL",
        defaultPosMode: BUSINESS_TYPES.SALON.defaultPosMode,
        defaultPriceMode: BUSINESS_TYPES.SALON.defaultPosMode ? "TTC" : "HT",
        psvbApplicable: BUSINESS_TYPES.SALON.psvbApplicable,
        categories: BUSINESS_TYPES.SALON.categories,
        audcifClass: "artisanale",
        defaultTaxGroup: "B",
        obligations: [
            {
                code: "CARTE_ARTISAN",
                label: "Carte professionnelle d'artisan (CMA-BF) — à renouveler",
                sourceStatus: "a_verifier",
                source: "Chambre des Métiers et de l'Artisanat — texte absent du dépôt",
            },
        ],
    },
    // A non-profit sells nothing over a counter and holds no merchandise:
    // its own vocabulary is funds, projects and donors, and its category
    // list is empty. The STOCK pack covers the one that distributes kits.
    SERVICE_INFORMATIQUE: {
        code: "SERVICE_INFORMATIQUE",
        label: "Services informatiques",
        description: "ESN, développement logiciel et maintenance",
        icon: "laptop",
        modules: [...CORE_MODULES],
        onboarding: [...CORE_ONBOARDING],
        terminology: {
            "articles.title": "Prestations",
            "articles.subtitle": "Gérez votre catalogue de prestations",
        },
        accountingSystem: "NORMAL",
        defaultPosMode: false,
        defaultPriceMode: "HT",
        psvbApplicable: false,
        categories: [],
        audcifClass: "services",
        defaultTaxGroup: "B",
    },
    // The one service profile that consumes raw material — paper and ink —
    // so it carries the purchasing group and the inventory the three others
    // do without. It sells no goods over a counter, hence no till.
    SERVICE_IMPRIMERIE: {
        code: "SERVICE_IMPRIMERIE",
        label: "Imprimerie",
        description: "Travaux d'impression, reprographie et façonnage",
        icon: "printer",
        modules: [...CORE_MODULES, "achats", "stock", "depots", "inventories"],
        onboarding: [...CORE_ONBOARDING],
        terminology: {
            "nav.stock": "Papier & consommables",
            "articles.title": "Travaux d'impression",
        },
        accountingSystem: "NORMAL",
        defaultPosMode: false,
        defaultPriceMode: "HT",
        psvbApplicable: false,
        categories: [],
        // Transforms paper and ink into the work it invoices, which is what
        // separates it from the three other service profiles.
        audcifClass: "artisanale",
        defaultTaxGroup: "B",
    },
    CONSEIL: {
        code: "CONSEIL",
        label: "Conseil / bureau d'études",
        description: "Prestations intellectuelles, audit et études",
        icon: "briefcase",
        modules: [...CORE_MODULES],
        onboarding: [...CORE_ONBOARDING],
        terminology: {
            "articles.title": "Prestations",
            "articles.subtitle": "Gérez votre catalogue de prestations",
        },
        accountingSystem: "NORMAL",
        defaultPosMode: false,
        defaultPriceMode: "HT",
        psvbApplicable: false,
        categories: [],
        audcifClass: "services",
        defaultTaxGroup: "B",
    },
    SANTE: {
        code: "SANTE",
        label: "Santé / cabinet médical",
        description: "Cabinet médical, clinique et centre de soins",
        icon: "stethoscope",
        modules: [...CORE_MODULES],
        onboarding: [...CORE_ONBOARDING],
        terminology: {
            "nav.clients": "Patients",
            "clients.title": "Patients",
            "articles.title": "Actes & prestations",
        },
        accountingSystem: "NORMAL",
        defaultPosMode: false,
        defaultPriceMode: "HT",
        psvbApplicable: false,
        categories: [],
        audcifClass: "services",
        defaultTaxGroup: "B",
    },
};
/**
 * Sector codes held by tenants that predate this registry, mapped to the
 * canonical profile each resolves to.
 *
 * Read-only vocabulary: `getSectorProfile` follows them and the settings
 * and signup schemas accept them, so a tenant stored under a legacy code
 * resolves a real profile and can save its own form. The UI option lists
 * derive from `SECTOR_PROFILES` alone, so no alias is ever offered as a
 * new choice.
 */
/**
 * Core's own profiles plus the one each vertical module contributes. A
 * capability module declares none and adds nothing here.
 */
export const SECTOR_PROFILES = composeProfiles(BASE_PROFILES, REGISTERED_MODULES);
export const SECTOR_PROFILE_ALIASES = {
    ONG: "ASSOCIATION",
    COMMERCE: "DETAIL",
    COMMERCE_DETAIL: "DETAIL",
    COMMERCE_GROS: "GROS",
    SERVICES: "CONSEIL",
};
/** Every code a tenant may submit: the canonical profiles and the aliases. */
export const ACCEPTED_SECTOR_CODES = [
    ...Object.keys(SECTOR_PROFILES),
    ...Object.keys(SECTOR_PROFILE_ALIASES),
];
export function getSectorProfile(code) {
    const canonical = SECTOR_PROFILE_ALIASES[code] ?? code;
    return SECTOR_PROFILES[canonical] ?? null;
}
export { CORE_MODULES, CORE_ONBOARDING } from "./core-modules.js";
//# sourceMappingURL=profiles.js.map